import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import moment from "moment";
import { Button } from "primereact/button";
import api from "../../services/api";
import Constants from "../../services/constants";
import { exportTableToCsv } from "../../utils/exportTableCsv";

export default function CallRecordingsTable({
    heading,
    allCallRecordings,
    loading,
    history,
    search,
    setSearch,
    page,
    limit,
    totalRecords,
    setPage,
    setLimit,
}) {
    const [exporting, setExporting] = useState(false);
    const formatDate = (date) => moment(date).format("MMMM Do YYYY, h:mm:ss a");

    const caller = (data) => {
        const userId = data?.caller?.userId;
        const userDBId = data?.caller?._id;
        const userPhone =
            data?.caller?.countryCode && data?.caller?.mobile
                ? `${data.caller.countryCode}${data.caller.mobile}`
                : null;
        return (
            <span
                style={{ cursor: "pointer" }}
                className="link"
                onClick={() => history(`/user/view/${userDBId}`)}
            >
                {userId} {userPhone ? `(${userPhone})` : null}
            </span>
        );
    };

    const reciever = (data) => {
        const userId = data?.reciever?.userId;
        const userDBId = data?.reciever?._id;
        const userPhone =
            data?.reciever?.countryCode && data?.reciever?.mobile
                ? `${data.reciever.countryCode}${data.reciever.mobile}`
                : null;
        return (
            <span
                style={{ cursor: "pointer" }}
                className="link"
                onClick={() => history(`/user/view/${userDBId}`)}
            >
                {userId} {userPhone ? `(${userPhone})` : null}
            </span>
        );
    };

    const recieverVehicle = (data) => data?.vehicle?.vehicleNumber || "-";

    const [audioDurations, setAudioDurations] = useState({});

    const handleLoadedMetadata = (id, e) => {
        const duration = Math.floor(e.target.duration);
        if (duration && !isNaN(duration) && duration > 0) {
            setAudioDurations((prev) => ({ ...prev, [id]: duration }));
        }
    };

    const callDuration = (rowData) => {
        const duration =
            audioDurations[rowData?._id] ??
            rowData?.recordingDuration ??
            rowData?.callDuration;
        if (!duration && duration !== 0) return "-";
        if (duration < 60) return `${duration}s`;
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
    };

    const recordingPlayer = (rowData) => {
        if (rowData?.recordingKeyMatchesCall === false) {
            return <span className="text-red-500">Recording mismatch</span>;
        }
        if (rowData?.playbackUrl) {
            return (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <audio
                    key={`${rowData._id}-${rowData.playbackUrl}`}
                    src={rowData.playbackUrl}
                    controls
                    preload="metadata"
                    onLoadedMetadata={(e) => handleLoadedMetadata(rowData._id, e)}
                    style={{ height: 34, maxWidth: 260 }}
                >
                    Your browser does not support audio playback.
                </audio>
            );
        }
        // Recording exists in DB but no signed URL (S3/creds not ready yet)
        if (rowData?.recordingUrl) {
            return <span className="text-500">Processing…</span>;
        }
        return <span className="text-500">-</span>;
    };

    const onPageChange = (event) => {
        setPage(event.page + 1);
        setLimit(event.rows);
    };

    const callerText = (data) => {
        const userId = data?.caller?.userId;
        const userPhone = data?.caller?.countryCode && data?.caller?.mobile
            ? `${data.caller.countryCode}${data.caller.mobile}`
            : null;
        return [userId, userPhone ? `(${userPhone})` : null].filter(Boolean).join(" ") || "-";
    };

    const recieverText = (data) => {
        const userId = data?.reciever?.userId;
        const userPhone = data?.reciever?.countryCode && data?.reciever?.mobile
            ? `${data.reciever.countryCode}${data.reciever.mobile}`
            : null;
        return [userId, userPhone ? `(${userPhone})` : null].filter(Boolean).join(" ") || "-";
    };

    // See CallLogsTable.jsx for why this bypasses the redux action.
    const exportAll = async () => {
        setExporting(true);
        try {
            const url = `${Constants.END_POINT.CALL_RECORDINGS}?page=1&limit=10000&search=${encodeURIComponent(search || "")}`;
            const res = await api("get", url);
            const rows = res?.data?.data || [];
            exportTableToCsv("call-recordings.csv", rows, [
                { header: "Caller", value: callerText },
                { header: "Reciever", value: recieverText },
                { header: "Vehicle Number", value: recieverVehicle },
                { header: "Duration", value: callDuration },
                { header: "Recording Available", value: (row) => (row?.recordingUrl ? "Yes" : "No") },
                { header: "Date and Time", value: (row) => formatDate(row.time) },
            ]);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="col-12">
            <div className="card">
                <div className="flex justify-content-between mb-4">
                    <h5>{heading}</h5>
                    <div className="flex align-items-center">
                        {setSearch ? (
                            <div className="p-input-icon-left mx-2">
                                <i className="pi pi-search" />
                                <InputText
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search"
                                />
                            </div>
                        ) : null}
                        <Button
                            label="Export CSV"
                            icon="pi pi-download"
                            onClick={exportAll}
                            loading={exporting}
                            disabled={!totalRecords}
                            severity="success"
                        />
                    </div>
                </div>
                <DataTable
                    dataKey="_id"
                    value={allCallRecordings}
                    loading={loading}
                    emptyMessage="No call recordings found."
                    showGridlines
                    first={(page - 1) * limit}
                    rows={limit}
                    totalRecords={totalRecords || 0}
                    onPage={onPageChange}
                >
                    <Column body={caller} header="Caller"></Column>
                    <Column body={reciever} header="Reciever"></Column>
                    <Column body={recieverVehicle} header="Vehicle Number"></Column>
                    <Column body={callDuration} header="Duration"></Column>
                    <Column body={recordingPlayer} header="Recording"></Column>
                    <Column
                        header="Date and Time"
                        field="time"
                        body={(rowData) => formatDate(rowData.time)}
                    />
                </DataTable>
                <Paginator
                    first={(page - 1) * limit}
                    rows={limit}
                    totalRecords={totalRecords || 0}
                    onPageChange={onPageChange}
                    rowsPerPageOptions={[10, 20, 30]}
                />
            </div>
        </div>
    );
}
